import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiHeart, FiTarget, FiAward, FiGlobe, FiTrendingUp } = FiIcons;

function About() {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: "easeOut" }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-rose-50 pt-16">
      {/* Hero Section */}
      <motion.section 
        className="py-20 px-4"
        initial="initial"
        animate="animate"
        variants={staggerContainer}
      >
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1 
            className="text-5xl md:text-6xl font-bold text-gray-900 mb-6"
            variants={fadeInUp}
          >
            About <span className="bg-gradient-to-r from-teal-500 via-cyan-500 to-rose-500 bg-clip-text text-transparent">2024</span>
          </motion.h1>
          <motion.p 
            className="text-xl md:text-2xl text-gray-600 font-light leading-relaxed"
            variants={fadeInUp}
          >
            We believe every moment deserves to be remembered, shared, and cherished forever.
          </motion.p>
        </div>
      </motion.section>

      {/* Mission Section */}
      <motion.section 
        className="py-20 px-4 bg-white"
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        variants={staggerContainer}
      >
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div variants={fadeInUp}>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Mission</h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                At 2024, we're on a mission to transform how people capture and share their most precious moments. We believe that memories shouldn't be scattered across different devices or lost in the digital void.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Our platform brings everyone together in one beautiful space where every photo, every video, and every memory can be shared instantly and preserved forever.
              </p>
            </motion.div>
            <motion.div 
              className="bg-gradient-to-br from-teal-100 to-cyan-100 rounded-3xl p-8"
              variants={fadeInUp}
            >
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-teal-600 mb-2">50K+</div>
                  <div className="text-gray-600">Events Created</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-cyan-600 mb-2">2M+</div>
                  <div className="text-gray-600">Photos Shared</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-rose-500 mb-2">100K+</div>
                  <div className="text-gray-600">Happy Users</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">99.9%</div>
                  <div className="text-gray-600">Uptime</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Values Section */}
      <motion.section 
        className="py-20 px-4"
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        variants={staggerContainer}
      >
        <div className="max-w-6xl mx-auto">
          <motion.h2 
            className="text-4xl font-bold text-center text-gray-900 mb-16"
            variants={fadeInUp}
          >
            Our Values
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: FiUsers,
                title: "Community First",
                description: "We believe in bringing people together through shared experiences and memories.",
                gradient: "from-teal-400 to-cyan-500"
              },
              {
                icon: FiHeart,
                title: "Passion Driven",
                description: "Every feature we build is crafted with love and attention to detail.",
                gradient: "from-cyan-400 to-blue-500"
              },
              {
                icon: FiTarget,
                title: "Purpose Focused",
                description: "We're committed to solving real problems and making memories accessible to everyone.",
                gradient: "from-blue-400 to-rose-500"
              }
            ].map((value, index) => (
              <motion.div 
                key={index}
                className="text-center"
                variants={fadeInUp}
              >
                <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                  <div className={`bg-gradient-to-r ${value.gradient} w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg`}>
                    <SafeIcon icon={value.icon} className="text-2xl text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Story Section */}
      <motion.section 
        className="py-20 px-4 bg-gradient-to-r from-gray-50 to-white"
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        variants={staggerContainer}
      >
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2 
            className="text-4xl font-bold text-gray-900 mb-8"
            variants={fadeInUp}
          >
            Our Story
          </motion.h2>
          <motion.div 
            className="text-lg text-gray-600 leading-relaxed space-y-6"
            variants={fadeInUp}
          >
            <p>
              It all started at a wedding in 2023. The bride and groom asked everyone to share their photos, 
              but collecting them became a nightmare. Photos were scattered across WhatsApp groups, email threads, 
              and social media platforms. Many beautiful moments were never shared.
            </p>
            <p>
              That's when we realized there had to be a better way. A simple, elegant solution that would make 
              sharing memories as easy as taking them. After months of development and testing with real events, 
              2024 was born.
            </p>
            <p>
              Today, we're proud to help thousands of people preserve their most precious moments, from intimate 
              gatherings to grand celebrations. Every photo shared on our platform represents a memory that will 
              never be lost.
            </p>
          </motion.div>
        </div>
      </motion.section>

      {/* Team Section */}
      <motion.section 
        className="py-20 px-4"
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        variants={staggerContainer}
      >
        <div className="max-w-6xl mx-auto">
          <motion.h2 
            className="text-4xl font-bold text-center text-gray-900 mb-16"
            variants={fadeInUp}
          >
            Meet Our Team
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: "Sarah Chen",
                role: "Co-Founder & CEO",
                bio: "Former product manager at tech giants, passionate about creating meaningful connections through technology.",
                image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=300&h=300&fit=crop&crop=face"
              },
              {
                name: "Marcus Rodriguez",
                role: "Co-Founder & CTO", 
                bio: "Full-stack engineer with 10+ years experience building scalable platforms that millions love to use.",
                image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=300&fit=crop&crop=face"
              },
              {
                name: "Emily Taylor",
                role: "Head of Design",
                bio: "Award-winning designer who believes that beautiful interfaces should make complex things feel simple.",
                image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=300&h=300&fit=crop&crop=face"
              }
            ].map((member, index) => (
              <motion.div 
                key={index}
                className="text-center"
                variants={fadeInUp}
              >
                <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                  <img 
                    src={member.image} 
                    alt={member.name}
                    className="w-24 h-24 rounded-full mx-auto mb-6 object-cover"
                  />
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{member.name}</h3>
                  <p className="text-cyan-600 font-semibold mb-4">{member.role}</p>
                  <p className="text-gray-600 leading-relaxed text-sm">{member.bio}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* CTA Section */}
      <motion.section 
        className="py-20 px-4 bg-gradient-to-r from-teal-500 via-cyan-500 to-blue-600"
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        variants={staggerContainer}
      >
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2 
            className="text-4xl md:text-5xl font-bold text-white mb-8"
            variants={fadeInUp}
          >
            Join Our Story
          </motion.h2>
          <motion.p 
            className="text-xl text-white/90 mb-8"
            variants={fadeInUp}
          >
            Be part of the movement to make memories infinite
          </motion.p>
          <motion.button 
            className="bg-white text-gray-900 px-12 py-4 rounded-full text-lg font-semibold shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105 transform"
            variants={fadeInUp}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Start Your Journey
          </motion.button>
        </div>
      </motion.section>
    </div>
  );
}

export default About;